import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * Crowd Heaven adjustment:
 * - Suppresses the sticky ATC while a Klaviyo bottom teaser/popup is visible on mobile.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {MutationObserver | null} */
  #popupMutationObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | undefined} */
  #popupCheckInterval;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#setupPopupObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    window.addEventListener('resize', this.#handleViewportChange, { signal });
    window.addEventListener('scroll', this.#handleViewportChange, { signal, passive: true });
    document.addEventListener('click', this.#handlePageClick, { signal });

    this.#getInitialQuantity();
    this.#syncStickyVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#popupMutationObserver?.disconnect();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    if (this.#popupCheckInterval) {
      clearInterval(this.#popupCheckInterval);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    this.#buyButtonsBlock = /** @type {HTMLElement} */ (buyButtonsBlock);

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting) {
        const rect = entry.target.getBoundingClientRect();

        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          if (this.#buyButtonsHaveBeenPassed()) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Watches the page for Klaviyo teaser/popup changes.
   */
  #setupPopupObserver() {
    if (!document.body) return;

    this.#popupMutationObserver = new MutationObserver(() => {
      this.#syncStickyVisibility();
    });

    this.#popupMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'aria-hidden'],
    });

    this.#popupCheckInterval = window.setInterval(() => {
      this.#syncStickyVisibility();
    }, 700);
  }

  /**
   * Public action handler for the sticky bar add to cart button.
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();
    this.#syncStickyVisibility();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete - resets puppet flag
   * @param {CustomEvent} _event - The cart event
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Keeps sticky state synced after scroll, resize, and popup changes.
   */
  #handleViewportChange = () => {
    this.#syncStickyVisibility();
  };

  /**
   * Rechecks after clicks, including closing Klaviyo teaser.
   */
  #handlePageClick = () => {
    window.setTimeout(() => this.#syncStickyVisibility(), 120);
    window.setTimeout(() => this.#syncStickyVisibility(), 500);
    window.setTimeout(() => this.#syncStickyVisibility(), 1100);
  };

  /**
   * Shows the sticky bar with animation.
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;

    if (this.#shouldHideForKlaviyo()) {
      this.#isStuck = false;
      stickyBar.dataset.stuck = 'false';
      stickyBar.setAttribute('data-klaviyo-suppressed', 'true');
      return;
    }

    stickyBar.removeAttribute('data-klaviyo-suppressed');
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation.
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Applies sticky visibility rules after page or popup changes.
   */
  #syncStickyVisibility() {
    if (this.#shouldHideForKlaviyo()) {
      this.#hideStickyBar();
      this.refs.stickyBar.setAttribute('data-klaviyo-suppressed', 'true');
      return;
    }

    this.refs.stickyBar.removeAttribute('data-klaviyo-suppressed');

    if (!this.#hiddenByBottom && this.#buyButtonsHaveBeenPassed()) {
      this.#showStickyBar();
    }
  }

  /**
   * Whether the main buy buttons have been scrolled past.
   * @returns {boolean}
   */
  #buyButtonsHaveBeenPassed() {
    if (!this.#buyButtonsBlock) return false;

    const rect = this.#buyButtonsBlock.getBoundingClientRect();

    return rect.bottom < 0 || rect.top < 0;
  }

  /**
   * Whether sticky ATC should hide because Klaviyo teaser/popup is visible.
   * @returns {boolean}
   */
  #shouldHideForKlaviyo() {
    return this.#isMobile() && this.#klaviyoBottomWidgetVisible();
  }

  /**
   * Mobile viewport check.
   * @returns {boolean}
   */
  #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * Basic visibility check.
   * @param {Element} el
   * @returns {DOMRect | null}
   */
  #visibleRect(el) {
    const rect = el.getBoundingClientRect();
    const styles = window.getComputedStyle(el);

    if (
      rect.width <= 0 ||
      rect.height <= 0 ||
      styles.display === 'none' ||
      styles.visibility === 'hidden' ||
      Number(styles.opacity) === 0
    ) {
      return null;
    }

    return rect;
  }

  /**
   * Detects Klaviyo bottom teaser/popup, including iframe-based widgets.
   * @returns {boolean}
   */
  #klaviyoBottomWidgetVisible() {
    const selectors = [
      '[class*="klaviyo"]',
      '[id*="klaviyo"]',
      'iframe[src*="klaviyo"]',
      '[aria-label*="Klaviyo"]',
      '[data-testid*="klaviyo"]',
      '[data-testid*="popup"]',
      '[data-testid*="teaser"]',
    ];

    const candidates = Array.from(document.body.querySelectorAll(selectors.join(',')));

    // Add broad fallback for bottom fixed overlays because Klaviyo can inject generic wrappers.
    const fixedCandidates = Array.from(document.body.querySelectorAll('*')).filter((el) => {
      if (el === this || this.contains(el)) return false;

      const styles = window.getComputedStyle(el);
      return styles.position === 'fixed' || styles.position === 'sticky';
    });

    const allCandidates = [...new Set([...candidates, ...fixedCandidates])];

    return allCandidates.some((el) => {
      if (el === this || this.contains(el)) return false;

      const rect = this.#visibleRect(el);
      if (!rect) return false;

      const styles = window.getComputedStyle(el);
      const text = (el.textContent || '').toLowerCase();
      const idClass = `${el.id || ''} ${typeof el.className === 'string' ? el.className : ''}`.toLowerCase();
      const src = String(el.getAttribute('src') || '').toLowerCase();
      const aria = String(el.getAttribute('aria-label') || '').toLowerCase();

      const looksLikeKlaviyo =
        idClass.includes('klaviyo') ||
        src.includes('klaviyo') ||
        aria.includes('klaviyo') ||
        text.includes('get 10% off') ||
        text.includes('10% off') ||
        text.includes('crowdheaven.com');

      const isBottomOverlay =
        (styles.position === 'fixed' || styles.position === 'sticky') &&
        rect.bottom >= window.innerHeight - 70 &&
        rect.top > window.innerHeight * 0.35 &&
        rect.height >= 40 &&
        rect.width >= window.innerWidth * 0.35;

      return looksLikeKlaviyo && isBottomOverlay;
    });
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}